<script setup lang="ts">
import { computed, watch, PropType, onMounted, nextTick, ref } from 'vue'
import { useOnboardingStore } from '@/store/onboarding/onboardingStore'
import { useRoute } from 'vue-router'
import router from '@/router'
import { useDisplay } from 'vuetify'

const onboarding = useOnboardingStore()
const route = useRoute()
const { smAndUp, mobile } = useDisplay()
const mobileLoading = ref(false)

const { id, location } = defineProps({
  id: String,
  location: {
    type: String as PropType<
      | 'top'
      | 'bottom'
      | 'start'
      | 'end'
      | 'left'
      | 'right'
      | 'top start'
      | 'top end'
      | 'bottom start'
      | 'bottom end'
    >,
    default: 'end',
  },
})

const shouldShow = computed(() => {
  if (!onboarding.step?.targetHtmlElement) return
  return onboarding.step.targetHtmlElement === id
})

const nextStep = async () => {
  if (mobile.value) {
    mobileLoading.value = true
    await onboarding.toggleOnboarding(true)
    mobileLoading.value = false
    return
  }

  try {
    await onboarding.nextStep()

    if (route.name !== step.value.page) {
      await router.push({ name: step.value.page })
      onboarding.manualProccess = false
    } else {
      onboarding.manualProccess = false
    }
  } catch (__) {}
}

const previousStep = async () => {
  try {
    await onboarding.previousStep()

    if (route.name !== step.value.page) {
      await router.push({ name: step.value.page })
      onboarding.manualProccess = false
    } else {
      onboarding.manualProccess = false
    }
  } catch (__) {}
}

const step = computed(() => onboarding.step)

onMounted(async () => {
  await nextTick()
  await onboarding.currentStep()
})

const normalizedLocation = computed(() => {
  return location.split(' ')[0] // ex: "top start" => "top"
})
</script>

<template>
  <v-tooltip
    v-if="shouldShow"
    eager
    offset="0"
    width="350"
    v-model:model-value="onboarding.visible"
    :open-on-hover="false"
    :open-on-focus="false"
    :open-on-click="false"
    :location="location"
    :content-class="`onboarding-tooltip elevation-14 ${normalizedLocation}`"
    persistent
  >
    <template #activator="{ props }">
      <div v-bind="props">
        <slot />
      </div>
    </template>

    <div>
      <div v-if="step.tooltipOrder <= 1" class="tooltip-header">
        {{ $t('onboarding.welcomeTitle') }}
      </div>
      <div class="d-flex flex-column ga-3" style="padding: 15.84px !important">
        <div class="tooltip-title">{{ step.title }}</div>
        <div v-if="!mobile" class="tooltip-desc">{{ step.description }}</div>
        <div v-else class="tooltip-desc">
          <span>{{ $t('onboarding.mobileTitle') }}</span>
          <br />
          <span>{{ $t('onboarding.mobileDesc') }}</span>
        </div>
        <div class="d-flex align-center justify-space-between">
          <span class="step-count">
            {{ $t('onboarding.stepCount', { value: step.tooltipOrder }) }}</span
          >
          <div class="d-flex ga-2 align-center">
            <div
              v-if="step.tooltipOrder > 1"
              class="text-gray-80 cursor-pointer action-btn"
              @click="() => previousStep()"
            >
              <div v-if="!onboarding.prevLoading">
                <v-icon color="#616161">mdi-chevron-left</v-icon>
                <span>{{ $t('onboarding.prev') }}</span>
              </div>
              <v-progress-circular
                v-else
                :size="18"
                indeterminate
                color="#616161"
                :width="3"
              ></v-progress-circular>
            </div>

            <v-btn
              v-if="!mobile"
              :loading="onboarding.nextLoading"
              color="secondary"
              rounded="lg"
              variant="flat"
              class="d-flex align-center action-btn cursor-pointer"
              @click="() => nextStep()"
            >
              <span>{{
                onboarding.step.targetHtmlElement ===
                'submit-account-validation-button'
                  ? $t('onboarding.finish')
                  : $t('onboarding.next')
              }}</span>
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              :loading="mobileLoading"
              v-else
              @click="() => nextStep()"
              rounded="lg"
              variant="flat"
              class="d-flex align-center action-btn cursor-pointer"
              color="secondary"
              >{{ $t('onboarding.agree') }}</v-btn
            >
          </div>
        </div>
      </div>
    </div>

    <v-btn
      @click="(onboarding.onboardingAlert = true), (onboarding.visible = false)"
      style="top: 9px; right: 5px"
      class="position-absolute"
      color="#7E652B"
      size="small"
      variant="text"
      density="compact"
      icon="$close"
    ></v-btn>
  </v-tooltip>

  <slot v-if="!shouldShow" />
</template>

<style lang="scss">
.onboarding-tooltip {
  background-color: #fffbf1 !important;
  padding: 0px !important;
  border-radius: 8.91px !important;
  color: #9e9e9e !important;
  border: 0.99px solid #e5e7eb;
  z-index: 3000 !important;
  pointer-events: auto !important;
}

// .v-tooltip > .v-overlay__content {
// padding: 0px !important;
// }

.onboarding-tooltip.end::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 8px solid #fffbf1;
}

@media (max-width: 600px) {
  .onboarding-tooltip.end::before {
    display: none;
  }
}

.onboarding-tooltip.left::before {
  content: '';
  position: absolute;
  right: -8px;
  top: 54%;
  transform: translateY(-90%);
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 8px solid #fffbf1;
}

.onboarding-tooltip.top::before {
  content: '';
  position: absolute;
  top: 100%; /* place la flèche en bas du tooltip */
  right: 70%; /* position horizontale personnalisée */
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #fffbf1;
}

.onboarding-tooltip.bottom::before {
  content: '';
  position: absolute;
  top: -8px; /* place la flèche en bas du tooltip */
  right: 50%; /* position horizontale personnalisée */
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid #fffbf1;
}

.tooltip-title {
  font-size: 15.84px;
  font-weight: 700;
  line-height: 140%;
  color: #029978;
}

.tooltip-desc {
  font-weight: 500;
  font-size: 12px;
  line-height: 140%;
  color: #0a0a0a;
}

.step-count {
  font-size: 15px;
  font-weight: 600;
  line-height: 140%;
  color: #0a0a0a;
}

.tooltip-header {
  padding: 12px 16px;
  background-color: #f3cc71;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  color: #7e652b;
}

.action-btn {
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.56px;
}
</style>
